import { Api } from '$api';
import { getApiUrl } from './asyncStorage';
import { buildEventPayload } from './index';
import { apiOptions } from './uploadRetry';

const QUEUE_KEY = 'upload_events_queue';
const QUEUE_LIMIT = 50;

function readQueue(): any[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]') || [];
  } catch {
    return [];
  }
}

function writeQueue(queue: any[]) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-QUEUE_LIMIT)));
  } catch {}
}

function post(payload: any) {
  return Api.post('/stats/bigquery_event', payload, apiOptions);
}

let flushing = false;

export async function flushUploadEvents() {
  if (flushing) return;
  const queue = readQueue();
  if (!queue.length) return;
  flushing = true;
  writeQueue([]);
  try {
    for (const payload of queue) {
      try {
        await post(payload);
      } catch {
        writeQueue([...readQueue(), payload]);
      }
    }
  } finally {
    flushing = false;
  }
}

function connectionInfo() {
  const connection = (navigator as any).connection;
  return {
    online: navigator.onLine,
    visibility: document.visibilityState,
    effective_type: connection?.effectiveType || '',
    is_pwa: window.matchMedia?.('(display-mode: standalone)')?.matches || false,
  };
}

// Sent through the regular API when possible; queued in localStorage and
// re-sent on `online` when the network is down (which is exactly when
// upload errors happen).
export function trackUploadEvent(name: string, params: Record<string, any> = {}) {
  const payload = buildEventPayload(name, { ...connectionInfo(), ...params });
  post(payload).catch(() => writeQueue([...readQueue(), payload]));
}

// Fire-and-forget on pagehide: sendBeacon cannot carry our auth header,
// so the caller passes user_id inside params.
export function beaconUploadEvent(name: string, params: Record<string, any> = {}) {
  const payload = buildEventPayload(name, { ...connectionInfo(), ...params });
  try {
    navigator.sendBeacon(
      `${getApiUrl()}/stats/bigquery_event`,
      new Blob([JSON.stringify(payload)], { type: 'text/plain' }),
    );
  } catch {}
}

let listening = false;
export function listenUploadEventsFlush() {
  if (listening) return;
  listening = true;
  window.addEventListener('online', () => {
    flushUploadEvents();
  });
  flushUploadEvents();
}
